import { useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { flyToUserProjectArea } from '../utils/cesiumHelpers';
import { eventBus } from '../events';
const PAN_RATE = 0.6;
const ROTATE_RATE = Math.PI / 3;
const TILT_RATE = Math.PI / 4;
const ZOOM_RATE = 0.7;
const ORIENT_DURATION = 0.5;
const EARTH_RADIUS_M = 6378137;
const MIN_PITCH = -Math.PI / 2;
const MAX_PITCH = 0;
const CONTINUOUS_KEYS = new Set([
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    '+', '=', '-', '_'
]);
const isTypingTarget = (target) => {
    if (!(target instanceof HTMLElement))
        return false;
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')
        return true;
    return target.isContentEditable;
};
const cameraHeight = (viewer) => {
    return Math.max(viewer.camera.positionCartographic.height, 100);
};
const panHorizontal = (viewer, forwardMeters, rightMeters) => {
    const camera = viewer.camera;
    const heading = camera.heading;
    const carto = camera.positionCartographic.clone();
    const sinH = Math.sin(heading);
    const cosH = Math.cos(heading);
    const dEast = forwardMeters * sinH + rightMeters * cosH;
    const dNorth = forwardMeters * cosH - rightMeters * sinH;
    const cosLat = Math.cos(carto.latitude);
    if (cosLat < 1e-6)
        return;
    carto.latitude += dNorth / EARTH_RADIUS_M;
    carto.longitude += dEast / (EARTH_RADIUS_M * cosLat);
    camera.setView({
        destination: Cesium.Cartographic.toCartesian(carto),
        orientation: {
            heading: camera.heading,
            pitch: camera.pitch,
            roll: camera.roll
        }
    });
};
const adjustOrientation = (viewer, headingDelta, pitchDelta) => {
    const camera = viewer.camera;
    const newPitch = Math.max(MIN_PITCH, Math.min(MAX_PITCH, camera.pitch + pitchDelta));
    camera.setView({
        destination: camera.positionWC.clone(),
        orientation: {
            heading: camera.heading + headingDelta,
            pitch: newPitch,
            roll: camera.roll
        }
    });
};
const flyOrientation = (viewer, heading, pitch) => {
    const camera = viewer.camera;
    viewer.camera.flyTo({
        destination: camera.positionWC.clone(),
        orientation: {
            heading: heading !== null && heading !== void 0 ? heading : camera.heading,
            pitch: pitch !== null && pitch !== void 0 ? pitch : camera.pitch,
            roll: 0.0
        },
        duration: ORIENT_DURATION
    });
};
const toolForToolKey = (key) => {
    switch (key.toLowerCase()) {
        case 'n': return 'placemark';
        case 'p': return 'polygon';
        case 't': return 'path';
        case 'm': return 'measure';
        case 'l': return 'label';
        default: return null;
    }
};
export function useGlobalKeyboardShortcuts(params) {
    const { viewer } = params;
    const pressedRef = useRef(new Set());
    const shiftRef = useRef(false);
    const rafRef = useRef(null);
    const lastTickRef = useRef(null);
    const viewerRef = useRef(viewer);
    // The listeners read the latest values through this ref and subscribe once.
    // Re-subscribing on every change stopped the held-key loop mid-press, and
    // the key's auto-repeat never restarted it, so a pan froze until re-pressed.
    const paramsRef = useRef(params);
    useEffect(() => {
        viewerRef.current = viewer;
    }, [viewer]);
    useEffect(() => {
        paramsRef.current = params;
    });
    useEffect(() => {
        const stopLoop = () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            lastTickRef.current = null;
        };
        const tick = (now) => {
            var _a;
            const v = viewerRef.current;
            if (paramsRef.current.modalOpen)
                pressedRef.current.clear();
            if (!v || v.isDestroyed() || pressedRef.current.size === 0) {
                stopLoop();
                return;
            }
            const last = (_a = lastTickRef.current) !== null && _a !== void 0 ? _a : now;
            const dt = Math.min((now - last) / 1000, 0.1);
            lastTickRef.current = now;
            const shift = shiftRef.current;
            const h = cameraHeight(v);
            for (const key of pressedRef.current) {
                switch (key) {
                    case 'ArrowLeft':
                        if (shift)
                            adjustOrientation(v, ROTATE_RATE * dt, 0);
                        else
                            panHorizontal(v, 0, -h * PAN_RATE * dt);
                        break;
                    case 'ArrowRight':
                        if (shift)
                            adjustOrientation(v, -ROTATE_RATE * dt, 0);
                        else
                            panHorizontal(v, 0, h * PAN_RATE * dt);
                        break;
                    case 'ArrowUp':
                        if (shift)
                            adjustOrientation(v, 0, -TILT_RATE * dt);
                        else
                            panHorizontal(v, h * PAN_RATE * dt, 0);
                        break;
                    case 'ArrowDown':
                        if (shift)
                            adjustOrientation(v, 0, TILT_RATE * dt);
                        else
                            panHorizontal(v, -h * PAN_RATE * dt, 0);
                        break;
                    case '+':
                    case '=':
                        v.camera.zoomIn(h * ZOOM_RATE * dt);
                        break;
                    case '-':
                    case '_':
                        v.camera.zoomOut(h * ZOOM_RATE * dt);
                        break;
                }
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        const startLoop = () => {
            if (rafRef.current !== null)
                return;
            lastTickRef.current = null;
            rafRef.current = requestAnimationFrame(tick);
        };
        const handleKeyDown = (e) => {
            const { activeTool, setTool, selectedProject, modalOpen, isDrawingActive } = paramsRef.current;
            if (isTypingTarget(e.target))
                return;
            if (modalOpen)
                return;
            if (e.key === 'Shift') {
                shiftRef.current = true;
                return;
            }
            const ctrlOrCmd = e.ctrlKey || e.metaKey;
            if (ctrlOrCmd && e.shiftKey) {
                const tool = toolForToolKey(e.key);
                if (tool) {
                    e.preventDefault();
                    setTool(tool);
                }
                return;
            }
            if (ctrlOrCmd || e.altKey)
                return;
            if (CONTINUOUS_KEYS.has(e.key)) {
                e.preventDefault();
                shiftRef.current = e.shiftKey;
                if (!pressedRef.current.has(e.key)) {
                    pressedRef.current.add(e.key);
                    startLoop();
                }
                return;
            }
            if (e.repeat)
                return;
            if (e.key === 'Escape') {
                if (activeTool && !isDrawingActive) {
                    e.preventDefault();
                    setTool(null);
                }
                return;
            }
            if (e.key === '?') {
                e.preventDefault();
                eventBus.emit('shortcuts:open');
                return;
            }
            if (!viewerRef.current || viewerRef.current.isDestroyed())
                return;
            const v = viewerRef.current;
            if (e.key === ' ') {
                e.preventDefault();
                v.camera.cancelFlight();
                return;
            }
            switch (e.key.toLowerCase()) {
                case 'n':
                    e.preventDefault();
                    flyOrientation(v, 0);
                    return;
                case 'u':
                    e.preventDefault();
                    flyOrientation(v, undefined, Cesium.Math.toRadians(-90));
                    return;
                case 'r':
                    e.preventDefault();
                    flyOrientation(v, 0, Cesium.Math.toRadians(-90));
                    return;
                case 'home':
                    if (selectedProject) {
                        e.preventDefault();
                        flyToUserProjectArea(v, selectedProject);
                    }
                    return;
            }
        };
        const handleKeyUp = (e) => {
            if (e.key === 'Shift') {
                shiftRef.current = false;
                return;
            }
            if (CONTINUOUS_KEYS.has(e.key)) {
                pressedRef.current.delete(e.key);
            }
        };
        const handleBlur = () => {
            pressedRef.current.clear();
            shiftRef.current = false;
            stopLoop();
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
            stopLoop();
        };
    }, []);
}
